/**
 * Mobil PDP commerce dock — tek satın alma yüzeyi.
 * Pickers: pdpDockUpdate(sectionId, payload)
 * Action: pdp-dock:action event
 */
(function () {
  'use strict';

  function pdpDockUpdate(sectionId, payload) {
    document.dispatchEvent(
      new CustomEvent('pdp-dock:update', {
        bubbles: true,
        detail: { sectionId: String(sectionId), ...payload },
      })
    );
  }

  window.pdpDockUpdate = pdpDockUpdate;

  class PdpMobileDock extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.mode = this.dataset.mode || 'form';
      this.defaultAtcLabel = this.dataset.atcLabel || 'Add to cart';
      this.selectLabel = this.dataset.selectLabel || 'Select designs';
      this.unitPriceCents = parseInt(this.dataset.unitPriceCents, 10) || 0;

      this.thumbEl = this.querySelector('.pdp-mobile-dock__thumb');
      this.selectionEl = this.querySelector('[data-dock-selection]');
      this.ctaBtn = this.querySelector('[data-dock-cta]');
      this.ctaLabelEl = this.querySelector('[data-dock-cta-label]');
      this.spinnerEl = this.querySelector('[data-dock-spinner]');
      this.noticeEl = this.querySelector('[data-dock-notice]');
      this.noticeTextEl = this.querySelector('[data-dock-notice-text]');

      this._ctaMode = 'add';
      this._ctaDisabled = this.ctaBtn?.disabled ?? false;
      this._noticeText = '';

      this._onDockUpdate = this._onDockUpdate.bind(this);
      this._onDockAction = this._onDockAction.bind(this);
      this._onVariantChange = this._onVariantChange.bind(this);

      document.addEventListener('pdp-dock:update', this._onDockUpdate);
      document.addEventListener('pdp-dock:action', this._onDockAction);

      this.ctaBtn?.addEventListener('click', () => {
        document.dispatchEvent(
          new CustomEvent('pdp-dock:action', {
            bubbles: true,
            detail: { sectionId: this.sectionId },
          })
        );
      });

      if (this.mode === 'form' && typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this._variantUnsub = subscribe(PUB_SUB_EVENTS.variantChange, this._onVariantChange);
      }

      if (this.hasAttribute('data-sold-out')) {
        this._applyCta({ ctaLabel: window.variantStrings?.soldOut || 'Sold out', ctaDisabled: true, ctaMode: 'add' });
      }
    }

    disconnectedCallback() {
      document.removeEventListener('pdp-dock:update', this._onDockUpdate);
      document.removeEventListener('pdp-dock:action', this._onDockAction);
      this._variantUnsub?.();
      clearTimeout(this._noticeTimer);
    }

    _root() {
      return document.getElementById(`MainProduct-${this.sectionId}`);
    }

    _onDockUpdate(event) {
      const { sectionId, thumbSrc, selectionLine, ctaLabel, ctaDisabled, ctaMode, loading, notice } =
        event.detail || {};
      if (sectionId !== this.sectionId) return;

      if (notice !== undefined) this._applyNotice(notice);

      if (thumbSrc && this.thumbEl?.tagName === 'IMG') {
        this.thumbEl.src = thumbSrc;
      }

      if (this.selectionEl) {
        if (selectionLine) {
          this.selectionEl.textContent = selectionLine;
          this.selectionEl.hidden = false;
        } else {
          this.selectionEl.textContent = '';
          this.selectionEl.hidden = true;
        }
      }

      if (loading !== undefined) {
        this.spinnerEl?.classList.toggle('hidden', !loading);
        if (this.ctaBtn) this.ctaBtn.disabled = loading || this._ctaDisabled;
      }

      if (ctaLabel !== undefined || ctaDisabled !== undefined || ctaMode !== undefined) {
        this._applyCta({
          ctaLabel: ctaLabel ?? this.ctaLabelEl?.textContent,
          ctaDisabled: ctaDisabled ?? this._ctaDisabled,
          ctaMode: ctaMode ?? this._ctaMode,
        });
      }
    }

    _applyCta({ ctaLabel, ctaDisabled, ctaMode }) {
      this._ctaMode = ctaMode;
      this._ctaDisabled = Boolean(ctaDisabled);
      if (this.ctaLabelEl && ctaLabel) this.ctaLabelEl.textContent = ctaLabel;
      if (this.ctaBtn) {
        this.ctaBtn.disabled = this._ctaDisabled;
        this.ctaBtn.classList.toggle('is-select-mode', ctaMode === 'scroll');
      }
    }

    /* Guidance pill above the bar. Two-phase on purpose: the text is written first and the .is-open
       class is added on the NEXT frame, otherwise the browser collapses the closed and open styles
       into one paint and the entrance never animates. On hide the class comes off immediately (the
       exit transition runs) and the text is only cleared once that transition is over, so it can't
       blank mid-animation. Bails out when the text hasn't changed, so repeated picker updates don't
       restart the animation or re-announce the live region. */
    _applyNotice(text) {
      if (!this.noticeEl || !this.noticeTextEl) return;
      const next = String(text || '').trim();
      if (next === this._noticeText) return;
      this._noticeText = next;
      clearTimeout(this._noticeTimer);

      if (next) {
        this.noticeTextEl.textContent = next;
        requestAnimationFrame(() => {
          if (this._noticeText) this.noticeEl.classList.add('is-open');
        });
      } else {
        this.noticeEl.classList.remove('is-open');
        this._noticeTimer = setTimeout(() => {
          if (!this._noticeText) this.noticeTextEl.textContent = '';
        }, 260);
      }
    }

    _onVariantChange({ data }) {
      if (!data || String(data.sectionId) !== this.sectionId) return;
      const variant = data.variant;
      if (!variant) return;

      const unitEl = this.querySelector('[data-dock-unit-price]');
      if (unitEl && window.Shopify?.formatMoney) {
        unitEl.textContent = window.Shopify.formatMoney(variant.price);
      }

      const compareEl = this.querySelector('[data-dock-compare-price]');
      if (compareEl && window.Shopify?.formatMoney) {
        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        if (onSale) {
          compareEl.textContent = window.Shopify.formatMoney(variant.compare_at_price);
          compareEl.hidden = false;
        } else {
          compareEl.textContent = '';
          compareEl.hidden = true;
        }
      }

      if (variant.featured_media?.preview_image?.src && this.thumbEl?.tagName === 'IMG') {
        this.thumbEl.src = variant.featured_media.preview_image.src;
      }

      const soldOut = !variant.available;
      this._applyCta({
        ctaLabel: soldOut ? window.variantStrings?.soldOut || 'Sold out' : this.defaultAtcLabel,
        ctaDisabled: soldOut,
        ctaMode: 'add',
      });
    }

    _onDockAction(event) {
      if (event.detail?.sectionId !== this.sectionId) return;

      const root = this._root();
      if (!root) return;

      if (this.mode === 'picker') {
        const picker =
          root.querySelector('aycan-design-picker') ||
          root.querySelector('aycan-coaster-picker') ||
          root.querySelector('aycan-keychain-picker');
        if (!picker) return;

        if (this._ctaMode === 'scroll') {
          picker.scrollIntoView({ behavior: 'smooth', block: 'start' });
          const first = picker.querySelector('.aycan-swatch__btn:not([disabled])');
          first?.focus({ preventScroll: true });
          return;
        }

        const atc =
          picker.querySelector('.aycan-design-picker__atc') ||
          picker.querySelector('.aycan-coaster__atc') ||
          picker.querySelector('.aycan-keychain-picker__atc');
        if (atc) {
          atc.click();
          return;
        }

        const submit =
          root.querySelector('.product-form__submit:not([disabled])') ||
          root.querySelector('form.product-form button[type="submit"]:not([disabled])');
        submit?.click();
        return;
      }

      const submit =
        root.querySelector('.product-form__submit:not([disabled])') ||
        root.querySelector('form.product-form button[type="submit"]:not([disabled])');
      submit?.click();
    }
  }

  customElements.define('pdp-mobile-dock', PdpMobileDock);
})();
